import { useState } from 'react';
import { ArrowLeft, Camera, Trash2, Upload, FileText, Download, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { packageService } from '../../../../../../services/postTripConnect/packageService';
import { getAssetUrl } from '../../../../../../utils/assetUtils';
import CustomSelect from '../../../../../../components/forms/CustomSelect';
import CustomInput from '../../../../../../components/forms/CustomInput';
import { postTripData } from '../../../../utils/postTripHelpers';

const PackageForm = ({
    editingPkg,
    viewOnly,
    setViewOnly,
    onClose,
    onSave
}) => {
    const regionOptions = postTripData.regionOptions || []
    const [uploading, setUploading] = useState(false);
    const [formData, setFormData] = useState({
        title: editingPkg?.title || '',
        description: editingPkg?.description || '',
        cover_image_url: editingPkg?.cover_image_url || '',
        attachment_url: editingPkg?.attachments?.[0]?.url || '',
        attachment_type: editingPkg?.attachments?.[0]?.type || 'pdf',
        destination_tag: editingPkg?.destination_tag || '',
        region: editingPkg?.region || 'domestic',
        status: editingPkg?.status || 'published'
    });

    const handleFileUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        try {
            setUploading(true);
            const res = await packageService.uploadFile(uploadFormData);

            const fileUrl = res?.url || res?.fileUrl || res?.data?.url;
            if (type === 'cover') {
                setFormData(prev => ({ ...prev, cover_image_url: fileUrl }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    attachment_url: fileUrl,
                    attachment_type: file.type.includes('pdf') ? 'pdf' : file.type.includes('image') ? 'image' : 'doc'
                }));
            }
            toast.success('File uploaded successfully');
        } catch (err) {
            console.error('File upload error:', err);
            toast.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e, saveStatus = null) => {
        if (e && e.preventDefault) e.preventDefault();
        const submitStatus = saveStatus || formData.status;

        if (!formData.title || !formData.description) {
            toast.error('Title and description are required');
            return;
        }

        if (formData.description.length > 300) {
            toast.error('Description must be under 300 characters');
            return;
        }

        const payload = {
            title: formData.title,
            description: formData.description,
            cover_image_url: formData.cover_image_url,
            destination_tag: formData.destination_tag,
            region: formData.region,
            status: submitStatus,
            attachments: formData.attachment_url ? [{
                url: formData.attachment_url,
                type: formData.attachment_type,
                filename: formData.title ? `${formData.title} Package.${formData.attachment_url.split('.').pop() || 'pdf'}` : (formData.attachment_url.split('/').pop() || 'itinerary')
            }] : []
        };

        await onSave(payload, editingPkg?._id);
    };

    return (
        <form onSubmit={handleSubmit} className="w-full space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            {/* Header Action Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 text-slate-500 hover:text-slate-800 rounded-xl transition-colors cursor-pointer"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-slate-800 tracking-tight">
                            {viewOnly ? 'View Package' : editingPkg ? 'Edit Package' : 'Create Package'}
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {viewOnly ? 'Preview details and attachments for this package' : editingPkg ? 'Update the details and files for your package' : 'Set up a new post-trip package for travellers'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {viewOnly ? (
                        <button
                            type="button"
                            onClick={() => setViewOnly(false)}
                            className="px-5 py-2.5 bg-[#067978] text-white text-xs font-bold rounded-xl hover:bg-[#056665] cursor-pointer shadow-lg shadow-[#067978]/20 transition-all active:scale-95"
                        >
                            Edit Package
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={(e) => handleSubmit(e, 'draft')}
                                disabled={uploading}
                                className="px-4 py-2.5 border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 cursor-pointer transition-colors"
                            >
                                Save as Draft
                            </button>
                            <button
                                type="button"
                                onClick={(e) => handleSubmit(e, 'published')}
                                disabled={uploading}
                                className="px-5 py-2.5 bg-[#067978] text-white text-xs font-bold rounded-xl hover:bg-[#056362] cursor-pointer shadow-sm transition-colors"
                            >
                                Publish Package
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Hero Cover Image (YouTube Style) */}
            <div className="w-full h-72 sm:h-80 md:h-96 rounded-2xl relative overflow-hidden bg-slate-100 border border-slate-200 shadow-inner group flex items-center justify-center">
                {formData.cover_image_url ? (
                    <>
                        <img
                            src={getAssetUrl(formData.cover_image_url)}
                            alt="Cover Banner"
                            className="w-full h-full object-cover"
                        />
                        {!viewOnly && (
                            <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-slate-950/50 transition-all duration-200 flex items-center justify-center gap-3">
                                <label className="px-4 py-2.5 bg-white/95 hover:bg-white text-slate-800 rounded-xl cursor-pointer transition-all shadow-lg flex items-center gap-2 border border-slate-200/50 hover:scale-105 font-bold text-xs">
                                    <Camera size={15} />
                                    Change Cover Photo
                                    <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'cover')} className="hidden" />
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, cover_image_url: '' }))}
                                    className="p-3 bg-white/95 hover:bg-rose-600 hover:text-white text-rose-600 rounded-full transition-all shadow-lg flex items-center justify-center hover:scale-110 border border-rose-100"
                                    title="Remove Cover Photo"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        )}
                    </>
                ) : viewOnly ? (
                    <div className="w-full h-full bg-gradient-to-br from-[#067978] to-teal-700 p-8 text-white flex flex-col justify-center items-center">
                        <ImageIcon size={48} className="text-teal-200/60 mb-2" />
                        <span className="font-bold text-lg">{formData.title || 'Travel Package'}</span>
                    </div>
                ) : (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-2xl bg-slate-50/50 hover:bg-slate-50 cursor-pointer transition-colors group h-full w-full p-8">
                        <div className="w-14 h-14 bg-white text-slate-400 group-hover:text-[#067978] group-hover:border-[#067978]/30 rounded-full flex items-center justify-center shadow-sm border border-slate-200 transition-colors mb-2.5">
                            <Upload size={24} />
                        </div>
                        <span className="text-base font-bold text-slate-700">Upload Cover Image</span>
                        <span className="text-xs text-slate-400 mt-1">Recommended: 16:9 Aspect Ratio (JPEG, PNG)</span>
                        <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'cover')} className="hidden" />
                    </label>
                )}
            </div>

            {/* Form Fields Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Main Fields Card */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
                    <CustomInput
                        label="Package title *"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g. Royal Rajasthan Circuit"
                        disabled={viewOnly}
                        required
                    />

                    <div>
                        <div className="flex justify-between items-center mb-1.5">
                            <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Description <span className="text-rose-500 ml-0.5">*</span></label>
                            {!viewOnly && (
                                <span className={`text-xs font-semibold ${formData.description.length > 300 ? 'text-rose-500' : 'text-slate-400'}`}>
                                    {formData.description.length}/300
                                </span>
                            )}
                        </div>
                        <textarea
                            rows={6}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Short summary that appears under the title in your traveller's app. Keep it crisp (max 300 chars)..."
                            maxLength={300}
                            disabled={viewOnly}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#067978]/20 focus:border-[#067978] resize-none disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                            required
                        />
                    </div>
                </div>

                {/* Settings Sidebar Card */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
                    <div>
                        <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Attachment (itinerary PDF/DOC)</label>
                        {formData.attachment_url ? (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4.5 space-y-4">
                                <div className="flex items-center gap-3.5 min-w-0">
                                    <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
                                        formData.attachment_type === 'pdf' ? 'bg-rose-50 text-rose-600 border border-rose-100' : 'bg-teal-50 text-[#067978] border border-teal-100'
                                    }`}>
                                        <FileText size={20} />
                                        <span className="text-[9px] font-black tracking-wider uppercase leading-none mt-0.5">
                                            {formData.attachment_type}
                                        </span>
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Attachment File</span>
                                        <span className="text-sm font-semibold text-slate-700 truncate" title={formData.attachment_url}>
                                            {formData.title ? `${formData.title} Package.${formData.attachment_url.split('.').pop() || 'pdf'}` : (formData.attachment_url.split('/').pop() || 'itinerary.pdf')}
                                        </span>
                                    </div>
                                </div>

                                {viewOnly ? (
                                    <a
                                        href={getAssetUrl(formData.attachment_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl cursor-pointer transition-colors border border-slate-200 flex items-center justify-center gap-1.5 shadow-sm"
                                    >
                                        <Download size={14} />
                                        Open Itinerary File
                                    </a>
                                ) : (
                                    <div className="flex items-center gap-2 pt-2 border-t border-slate-200/60">
                                        <label className="flex-1 py-2 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-lg cursor-pointer transition-colors border border-slate-200 flex items-center justify-center gap-1.5 shadow-xs" title="Change File">
                                            <Upload size={14} />
                                            Replace
                                            <input type="file" accept=".pdf,.doc,.docx,image/*" onChange={(e) => handleFileUpload(e, 'attachment')} className="hidden" />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, attachment_url: '', attachment_type: 'pdf' }))}
                                            className="flex-shrink-0 p-2 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white rounded-lg transition-colors border border-rose-100 flex items-center justify-center"
                                            title="Remove File"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : viewOnly ? (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4.5 text-center">
                                <FileText size={24} className="mx-auto text-slate-400 mb-1.5" />
                                <span className="text-xs font-semibold text-slate-500">No itinerary attached</span>
                            </div>
                        ) : (
                            <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-6 bg-slate-50/50 hover:bg-slate-50 cursor-pointer transition-colors group">
                                <div className="w-10 h-10 bg-white text-slate-400 group-hover:text-[#067978] group-hover:border-[#067978]/30 rounded-full flex items-center justify-center shadow-sm border border-slate-200 transition-colors mb-2">
                                    <FileText size={18} />
                                </div>
                                <span className="text-xs font-bold text-slate-700">Upload Itinerary File</span>
                                <span className="text-[10px] text-slate-400 mt-1">PDF, DOCX, or Image</span>
                                <input type="file" accept=".pdf,.doc,.docx,image/*" onChange={(e) => handleFileUpload(e, 'attachment')} className="hidden" />
                            </label>
                        )}
                    </div>

                    <div>
                        <CustomSelect
                            label="Region"
                            value={formData.region}
                            onChange={(val) => setFormData({ ...formData, region: val })}
                            options={regionOptions}
                            disabled={viewOnly}
                            placeholder="Select Region"
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default PackageForm;
